import camelCase from 'lodash/camelCase';
import snakeCase from 'lodash/snakeCase';
import { DateTime } from 'luxon';
import type { MatterField } from '../constants/matterFields';
import { getMatterField, getMatterFieldWidget, type MatterFieldWidget } from './matterFields';

export const LEGAL_DOC_DATE_FILL_FORMAT = 'MM-dd-yyyy';

/** Tags filled from prospect/user/contact context, never shown as extra inputs. */
export const AUTO_FILLED_LEGAL_DOC_TAGS = new Set([
    'custom_file_number',
    'lawyer_name',
    'lawyer_signature',
    'lawyer_email',
    'cc_user_name',
    'cc_user_email',
    'assigned_user',
    'assigned_user_email',
    'prospect_name',
    'practice_area',
    'matter_type',
    'prospect_created_datetime',
    'prospect_created_date',
    'created_time',
    'client_name',
    'client_first_name',
    'client_last_name',
    'all_client_names',
    'client_email',
    'client_signature',
    'date',
]);

/**
 * Tags stored as typed prospect_overrides columns instead of in the legalDocPrefills bag. Other
 * names of these fields stay in the bag, so adding an alias never moves a saved value.
 */
const FIRST_CLASS_LEGAL_DOC_TAGS = new Set([
    'retainer_amount',
    'retained_amount',
    'subject_address',
    'closing_date',
    'purchase_price',
    'sale_price',
]);

function getFirstClassField(tag: string): MatterField | undefined {
    return FIRST_CLASS_LEGAL_DOC_TAGS.has(tag) ? getMatterField(tag) : undefined;
}

/** Expands values keyed by canonical first-class tag to every first-class tag of that field. */
export function expandFirstClassLegalDocTags<T>(
    byCanonicalTag: Record<string, T>,
): Record<string, T> {
    const expanded: Record<string, T> = {};
    for (const tag of FIRST_CLASS_LEGAL_DOC_TAGS) {
        const field = getMatterField(tag);
        const canonicalTag = field ? snakeCase(field.key) : undefined;
        if (canonicalTag && canonicalTag in byCanonicalTag) {
            expanded[tag] = byCanonicalTag[canonicalTag];
        }
    }
    return expanded;
}

export const OTHER_PARTY_NAMES_TAG = 'other_party_names';
export const SELLERS_TAG = 'sellers';
export const BUYERS_TAG = 'buyers';
export const PURCHASERS_TAG = 'purchasers';

export const PARTY_NAME_LEGAL_DOC_TAGS = [
    OTHER_PARTY_NAMES_TAG,
    SELLERS_TAG,
    BUYERS_TAG,
    PURCHASERS_TAG,
] as const;

export type PartyNameLegalDocTag = (typeof PARTY_NAME_LEGAL_DOC_TAGS)[number];

function isSaleMatter(matterType: string | null | undefined): boolean {
    return matterType === 'Sale';
}

export function isPartyNameLegalDocTag(tag: string): tag is PartyNameLegalDocTag {
    return (PARTY_NAME_LEGAL_DOC_TAGS as readonly string[]).includes(tag);
}

/**
 * `sellers` is the other party on a purchase and the clients on a sale.
 * `buyers` / `purchasers` are the inverse. `other_party_names` is always the opposite side.
 */
export function getLegalDocPartyNamePrefills(params: {
    matterType: string | null | undefined;
    allClientNames: string;
    otherPartyNames: string;
}): Record<PartyNameLegalDocTag, string> {
    const allClientNames = params.allClientNames.trim();
    const otherPartyNames = params.otherPartyNames.trim();
    const isSale = isSaleMatter(params.matterType);

    return {
        other_party_names: otherPartyNames,
        sellers: isSale ? allClientNames : otherPartyNames,
        buyers: isSale ? otherPartyNames : allClientNames,
        purchasers: isSale ? otherPartyNames : allClientNames,
    };
}

export function getOtherPartyNamesFromPrefills(
    prefills: Record<string, string> | null | undefined,
    matterType: string | null | undefined,
): string {
    const bag = prefills ?? {};
    const fromCanonical = bag[OTHER_PARTY_NAMES_TAG]?.trim();
    if (fromCanonical) {
        return fromCanonical;
    }

    if (isSaleMatter(matterType)) {
        return bag[BUYERS_TAG]?.trim() || bag[PURCHASERS_TAG]?.trim() || '';
    }

    return bag[SELLERS_TAG]?.trim() || '';
}

/**
 * Party name tags that always fill with the clients' names (`sellers` on a sale, `buyers` /
 * `purchasers` otherwise). The document fill replaces them with the contacts' names.
 */
export function isClientPartyNameLegalDocTag(
    tag: string,
    matterType: string | null | undefined,
): boolean {
    return isSaleMatter(matterType)
        ? tag === SELLERS_TAG
        : tag === BUYERS_TAG || tag === PURCHASERS_TAG;
}

/** The matter field holding the other side's names: the purchasers on a sale, else the sellers. */
export function getOtherPartyNamesFieldKey(
    matterType: string | null | undefined,
): 'purchaserNames' | 'sellerNames' {
    return isSaleMatter(matterType) ? 'purchaserNames' : 'sellerNames';
}

/**
 * Key of the value a legal doc tag shows and edits: the other side's names field for party name
 * tags, the matter field key for known fields (so aliases share a value), or the tag itself.
 */
export function getLegalDocValueKey(tag: string, matterType: string | null | undefined): string {
    if (isPartyNameLegalDocTag(tag)) {
        return getOtherPartyNamesFieldKey(matterType);
    }
    return getMatterField(tag)?.key ?? tag;
}

export function isExtraLegalDocTag(tag: string): boolean {
    return !AUTO_FILLED_LEGAL_DOC_TAGS.has(tag) && !getFirstClassField(tag);
}

export function getExtraLegalDocTags(placeholders: string[] | null | undefined): string[] {
    if (!placeholders?.length) {
        return [];
    }

    const seen = new Set<string>();
    const extraTags: string[] = [];

    for (const tag of placeholders) {
        if (!isExtraLegalDocTag(tag) || seen.has(tag)) {
            continue;
        }
        seen.add(tag);
        extraTags.push(tag);
    }

    return extraTags;
}

/**
 * Tags the user fills in when creating a prospect, in template order. Tags that share a value
 * (e.g. `purchase_price` / `sale_price`, or `sellers` / `other_party_names` on a purchase)
 * appear once. Client party name tags are left out because they fill from the clients.
 */
export function getEditableLegalDocTags(
    placeholders: string[] | null | undefined,
    matterType: string | null | undefined,
): string[] {
    if (!placeholders?.length) {
        return [];
    }

    const seen = new Set<string>();
    const editableTags: string[] = [];

    for (const tag of placeholders) {
        if (AUTO_FILLED_LEGAL_DOC_TAGS.has(tag) || isClientPartyNameLegalDocTag(tag, matterType)) {
            continue;
        }
        const key = getLegalDocValueKey(tag, matterType);
        if (seen.has(key)) {
            continue;
        }
        seen.add(key);
        editableTags.push(tag);
    }

    return editableTags;
}

/**
 * The legalDocPrefills bag to save from values keyed by `getLegalDocValueKey`: every extra tag
 * in the template, plus `other_party_names` when `includeOtherPartyNames` so a later template can
 * fill the other side. Only include it while that value is shown (with a scan), so a value the
 * user can no longer see isn't saved.
 */
export function buildLegalDocPrefillsFromValues(
    placeholders: string[] | null | undefined,
    values: Record<string, string>,
    matterType: string | null | undefined,
    { includeOtherPartyNames }: { includeOtherPartyNames: boolean },
): Record<string, string> {
    const bag: Record<string, string> = {};
    if (includeOtherPartyNames) {
        bag[OTHER_PARTY_NAMES_TAG] = values[getOtherPartyNamesFieldKey(matterType)] ?? '';
    }
    for (const tag of getExtraLegalDocTags(placeholders)) {
        if (!isClientPartyNameLegalDocTag(tag, matterType)) {
            bag[tag] = values[getLegalDocValueKey(tag, matterType)] ?? '';
        }
    }
    return compactLegalDocPrefills(bag);
}

/** Known fields use their field type. Unknown tags fall back to suffix heuristics. */
export function getLegalDocPrefillWidget(tag: string): MatterFieldWidget {
    const field = getMatterField(tag);
    if (field) {
        return getMatterFieldWidget(field);
    }
    if (tag.endsWith('_address')) {
        return 'address';
    }
    if (tag.endsWith('_date')) {
        return 'date';
    }
    if (tag.endsWith('_amount') || tag.endsWith('_price')) {
        return 'currency';
    }
    return 'text';
}

/** Aliases keep their own wording, so `sale_price` is not labelled "Purchase Price". */
export function getLegalDocPrefillLabel(tag: string): string {
    const field = getMatterField(tag);
    if (field && camelCase(tag) === field.key) {
        return field.label;
    }

    return tag
        .split('_')
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

export function formatLegalDocPrefillFillValue(tag: string, value: string): string {
    const trimmed = value.trim();
    if (!trimmed) {
        return '';
    }

    const widget = getLegalDocPrefillWidget(tag);
    if (widget === 'date') {
        const dateTime = DateTime.fromISO(trimmed);
        return dateTime.isValid ? dateTime.toFormat(LEGAL_DOC_DATE_FILL_FORMAT) : '';
    }
    if (widget === 'currency') {
        return `$${trimmed}`;
    }

    return trimmed;
}

export function formatExtraLegalDocPrefills(
    prefills: Record<string, string> | null | undefined,
): Record<string, string> {
    const formatted: Record<string, string> = {};

    for (const [tag, value] of Object.entries(prefills ?? {})) {
        if (!isExtraLegalDocTag(tag)) {
            continue;
        }
        const fillValue = formatLegalDocPrefillFillValue(tag, value);
        if (fillValue) {
            formatted[tag] = fillValue;
        }
    }

    return formatted;
}

/** Whether a non-empty value matches the format its widget stores (ISO dates, numeric currency). */
export function isValidLegalDocPrefillValue(tag: string, value: string): boolean {
    const widget = getLegalDocPrefillWidget(tag);
    if (widget === 'date') {
        return /^\d{4}-\d{2}-\d{2}$/.test(value) && DateTime.fromISO(value).isValid;
    }
    if (widget === 'currency') {
        return !Number.isNaN(Number.parseFloat(value));
    }
    return true;
}

export function normalizeLegalDocPrefills(
    bag: Record<string, string> | null | undefined,
): Record<string, string> | undefined {
    if (!bag) {
        return undefined;
    }

    const normalized: Record<string, string> = {};

    for (const [tag, raw] of Object.entries(bag)) {
        const value = raw.trim();
        if (!value || !isValidLegalDocPrefillValue(tag, value)) {
            continue;
        }

        normalized[tag] = value;
    }

    return Object.keys(normalized).length > 0 ? normalized : undefined;
}

export function compactLegalDocPrefills(
    bag: Record<string, string> | null | undefined,
): Record<string, string> {
    const compact: Record<string, string> = {};

    for (const [tag, raw] of Object.entries(bag ?? {})) {
        const value = raw.trim();
        if (value && isExtraLegalDocTag(tag)) {
            compact[tag] = value;
        }
    }

    return compact;
}
